import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLine, entryConnector, moduleSides, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_02 – long straight 40 m (design/01_segments/SEG_02_Straight_40M). Calm and
 * early-game friendly: rows every 10 m, no double jumps.
 *
 *   Obstacle rows:  R05 z=5, R15 z=15, R25 z=25, R35 z=35   (sparse = R15 + R35, dense = all four)
 *   Collectibles:   staggered lines middle ↔ right, switching lanes every 10 m
 *   Visual:         two SEG_STRAIGHT_20M road modules (same look as the base module)
 */
const LENGTH = 40;
const BLOCK = 10;
const CALM_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;
const MIDDLE = 1;
const RIGHT = 2;
const SPACING = 2;
const flat = () => COLLECTIBLE_FLOAT_HEIGHT;

/** Lane per 10 m block: alternating between two lanes, starting with `first`. */
function staggered(line: string, first: number, second: number) {
  return collectibleLine(
    WORLD_CONFIG,
    line,
    Array.from({ length: LENGTH / BLOCK }, (_, block) => ({
      lane: block % 2 === 0 ? first : second,
      from: block * BLOCK + 1,
      to: block * BLOCK + BLOCK - 1,
      spacing: SPACING,
      height: flat,
    })),
  );
}

export const SEG_STRAIGHT_40M: SegmentDefinition = {
  id: 'SEG_STRAIGHT_40M',
  kind: SegmentKind.Straight,
  displayName: 'Gerade 40 m',
  length: LENGTH,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [{ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } }],

  entry: entryConnector(),
  exits: [straightExit(LENGTH)],
  lanes: standardLanes(WORLD_CONFIG),

  obstacleSpawnPoints: [
    ...obstacleRow(WORLD_CONFIG, 'R05', 5, CALM_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'R15', 15, CALM_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'R25', 25, CALM_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'R35', 35, CALM_TYPES),
  ],
  obstacleRowLayouts: { sparse: ['R15', 'R35'], dense: ['R05', 'R15', 'R25', 'R35'] },

  collectibleSpawnPoints: [...staggered('MIDDLE_RIGHT', MIDDLE, RIGHT), ...staggered('RIGHT_MIDDLE', RIGHT, MIDDLE)],

  ...moduleSides(LENGTH),

  generation: {
    weight: 0.6,
    minDistance: 40,
    family: 'STRAIGHT_40',
    tags: ['straight', 'ground', 'long'],
  },
};
